/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Grid, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { InactivityHandler } from "@components/InactivityHandler";
import { useEffect, useRef, useState } from "react";
import { useLazyGetPersonsQuery } from "@api/personasApi";
import { useLazyGetMetaMapDataQuery } from "@api/metaMapApi";
import { MetaDataJson } from "@interfaces/catalogs";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError, PersonaStructure } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";

interface ValidarIdentidadProps {
  person: "REP_LEGAL" | "AVAL" | "CONYUGE_AVAL" | "AVAL_ADICIONAL";
  regimenType?: string;
}

const ValidacionIdentidadPersona: React.FC<ValidarIdentidadProps> = (props) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const metamapButtonRef = useRef<HTMLDivElement>(null);

  const applicationData = useSelector((state: RootState) => state.register.application);
  const personAval = useSelector((state: RootState) => state.bofu.aval);
  const personAvalConyuge = useSelector((state: RootState) => state.bofu.aval_conyuge);
  const representanteId = useSelector((state: RootState) => state.bofu.validation_representante_id);
  const avalAdicionalId = useSelector((state: RootState) => state.bofu.aval_adicional_id_validation);

  const [getDataMetaMap] = useLazyGetMetaMapDataQuery();
  const [getPersons] = useLazyGetPersonsQuery();

  const [personName, setPersonName] = useState("");
  const [enableButton, setEnableButton] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [metaDataJson, setMetaDataJson] = useState<MetaDataJson | null>(null);
  const [identityLegend, setIdentityLegend] = useState<string | null>(null);

  const goBack = () => {
    if (props.regimenType === "PFAE") {
      navigate("/producto/creditosimple/avance_expediente_digitalPFAE");
      return;
    }

    switch (props.person) {
      case "REP_LEGAL":
        navigate("/producto/creditosimple/avance_expediente_digital");
        break;
      case "CONYUGE_AVAL":
      default:
        navigate("/producto/creditosimple/docs_aval");
        break;
    }
  };

  const getMetamapData = async (person_selected: PersonaStructure) => {
    setIsLoading(true);

    setPersonName([person_selected.name, person_selected.last_name, person_selected.last_name_2].join(" ").trim());

    const metamap_res = await getDataMetaMap(person_selected.id).unwrap();
    const metamap_info = metamap_res?.data;

    if (metamap_info.attempts > 3) {
      return;
    }

    setMetaDataJson({
      client_details_id: metamap_info.client_details_id,
      loan_application_id: metamap_info.loan_application_id,
      person_id: metamap_info.person_id,
      person_type: metamap_info.person_type,
      regimen: metamap_info.regimen,
      attempts: metamap_info.attempts,
      upload_type: metamap_info.upload_type,
    });

    setEnableButton(true);
  };

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        let personId = undefined;

        if (props.person === "REP_LEGAL") {
          personId = representanteId;
          setIdentityLegend(
            "En caso de no ser tú el representante legal, podrás reenviar la solicitud de validación vía correo al iniciar el proceso.",
          );
        } else if (props.person === "AVAL") {
          personId = personAval?.id;
          setIdentityLegend(
            "En caso de no ser tú el Aval de la Empresa, podrás reenviar la solicitud de validación vía correo al iniciar el proceso.",
          );
        } else if (props.person === "CONYUGE_AVAL") {
          personId = personAvalConyuge?.id;
          setIdentityLegend(
            "En caso de no ser tú el Conyuge del Aval, podrás reenviar la solicitud de validación vía correo al iniciar el proceso.",
          );
        } else if (props.person === "AVAL_ADICIONAL") {
          personId = avalAdicionalId;
          setIdentityLegend(
            "En caso de no ser tú el Aval Adicional, podrás reenviar la solicitud de validación vía correo al iniciar el proceso.",
          );
        } else {
          console.error("Tipo de persona no válido");
          goBack();
          return;
        }

        if (!personId) {
          console.error("No hay persona seleccionada");
          goBack();
          return;
        }

        const person_list = await getPersons({
          loanApplicationId: applicationData.id,
          id: personId,
        }).unwrap();

        const person_selected = person_list[0];

        if (!person_selected) {
          console.error("No se encontró la persona seleccionada");
          goBack();
          return;
        }

        if (person_selected.identity_validation_completed) {
          console.error("La persona ya tiene la validación de identidad completada");
          goBack();
          return;
        }

        await getMetamapData(person_selected);
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "ValidacionIdentidadPersona-initial_fetch",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleUserFinishedSdk = (_event: CustomEvent) => {
      setMetaDataJson(null);
      setEnableButton(false);
      goBack();
    };

    const metamapButton = metamapButtonRef.current;

    if (metamapButton) {
      metamapButton.addEventListener("metamap:userFinishedSdk", handleUserFinishedSdk as EventListener);
    }

    return () => {
      if (metamapButton) {
        metamapButton.removeEventListener("metamap:userFinishedSdk", handleUserFinishedSdk as EventListener);
      }
    };
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        <Box textAlign={"center"} mx={4} mt={4}>
          <Typography
            textAlign={"center"}
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={700}
            color={theme?.palette?.primary?.dark}
          >
            Validación identidad
          </Typography>

          {isLoading ? (
            <Box mt={3}>
              <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
            </Box>
          ) : (
            <Box mt={3}>
              <Typography
                textAlign={"center"}
                variant={isMobileOrTablet ? "body2" : "body1"}
                fontWeight={400}
                color={theme?.palette?.primary?.main}
                mt={4}
              >
                {personName || ""}
              </Typography>

              <Typography
                textAlign={"center"}
                variant={isMobileOrTablet ? "body2" : "body1"}
                fontWeight={600}
                color={theme?.palette?.primary?.dark}
                mt={4}
              >
                Validaremos tu identidad por medio de fotografías faciales y tu identificación oficial
              </Typography>

              <Typography
                textAlign={"center"}
                variant={isMobileOrTablet ? "body2" : "body1"}
                fontWeight={400}
                color={theme?.palette?.primary?.dark}
                mt={4}
              >
                {identityLegend}
              </Typography>

              <Box mt={4} sx={{ pointerEvents: isLoading ? "none" : "auto", opacity: isLoading ? 0.5 : 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    pointerEvents: !enableButton ? "none" : "auto",
                    opacity: !enableButton ? 0.5 : 1,
                    cursor: !enableButton ? "not-allowed" : "pointer",
                  }}
                >
                  <metamap-button
                    ref={metamapButtonRef}
                    clientid={import.meta.env.VITE_META_CLIENT_ID}
                    flowId={import.meta.env.VITE_META_FLOW_ID}
                    merchantToken={import.meta.env.VITE_MERCHANT_TOKEN}
                    language="es"
                    metadata={JSON.stringify(metaDataJson || {})}
                    color={theme?.palette?.primary?.main}
                  />
                </Box>
              </Box>

              <Box display="flex" justifyContent="center" mt={4}>
                <Button variant="blue_outlined" type="button" onClick={goBack}>
                  Regresar al expediente
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default ValidacionIdentidadPersona;
